/**
 * Proposal detail blocks (spec 3.12) and the decision controls (3.13). Every decision
 * shows its evidence: the customer's question, what the Denetçi checked, the sources and
 * the exact outgoing text or diff.
 */

import type { Ref } from 'react';
import type {
  Check,
  CouponProposal,
  CustomerReplyProposal,
  GenericProposal,
  ListingTitleProposal,
  Proposal,
  Reasoning,
  Run,
  SourceRef,
} from '../api/types.js';
import { usePanel, useStore } from '../app/context.js';
import { Link } from '../app/router.js';
import { AGENT_NAMES, AGENT_SHAPES, canEdit, requiresHold } from '../app/selectors.js';
import { t4HoldMs } from '../app/settings.js';
import type { ApproveBlocker } from '../app/store.js';
import { formatClock, formatDuration, formatSecondsLeft, formatUsd } from '../lib/format.js';
import type { ApproveVia } from '../screens/decision.js';
import { type HoldHandle, T4HoldButton } from './HoldButton.js';
import { AgentGlyph, Icon } from './icons.js';
import { Kbd, TriangleGlyph } from './primitives.js';
import { copyText } from './sheets.js';

export function QuotedInput({ proposal, compact = false }: { proposal: CustomerReplyProposal; compact?: boolean }) {
  const store = useStore();
  return (
    <figure className={compact ? 'quote quote--compact' : 'quote'}>
      <div className="quote__head">
        <figcaption className="key ink-2 nowrap">{proposal.input.key}</figcaption>
        {compact ? <ScreenshotButton onClick={() => screenshotToast(store)} /> : null}
      </div>
      {proposal.input.quoteTr ? (
        <>
          <blockquote className="quote__text">{proposal.input.quoteTr}</blockquote>
          <p lang="en" className="quote__tr">
            <span className="key ink-2">Orijinal</span> {proposal.input.quote}
          </p>
        </>
      ) : (
        <blockquote lang="en" className="quote__text">
          {proposal.input.quote}
        </blockquote>
      )}
      {compact ? null : (
        <div className="quote__foot">
          <span className="ellipsis">{proposal.input.note}</span>
          <ScreenshotButton onClick={() => screenshotToast(store)} />
        </div>
      )}
    </figure>
  );
}

function screenshotToast(store: ReturnType<typeof useStore>): void {
  store.showToast('Ekran görüntüsü sunucudaki orijinalden açılır; bu önizlemede yok', { icon: 'info' });
}

function ScreenshotButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="link-btn quote__shot" onClick={onClick}>
      <Icon name="image" size={16} />
      Ekran görüntüsü
    </button>
  );
}

export function CheckStrip({ checks, fact }: { checks: Check[]; fact?: Check | null }) {
  const cells = fact ? [fact, ...checks] : checks;
  return (
    <ul className={`checks checks--${cells.length}`} aria-label="Kontroller">
      {cells.map((check) => (
        <li key={check.key} className="checks__cell">
          <div className="checks__key key ink-2">
            {check.result === 'passed' ? <Icon name="check" size={10} stroke={3} className="ok" /> : null}
            {check.result === 'rejected' ? <Icon name="x" size={10} stroke={3} className="danger" /> : null}
            {check.result === 'unverified' ? (
              <span className="warning">
                <TriangleGlyph />
              </span>
            ) : null}
            {check.key}
            {check.result === 'passed' ? <span className="sr-only"> geçti</span> : null}
          </div>
          <div className="checks__value ellipsis">
            {check.value}
            {check.valueMono ? <span className="mono checks__mono"> {check.valueMono}</span> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function DraftBlock({ proposal, flat = false }: { proposal: CustomerReplyProposal; flat?: boolean }) {
  const store = useStore();
  const headingId = `draft-${proposal.id}`;
  return (
    <section className={flat ? 'draft draft--flat' : 'draft'} aria-labelledby={headingId}>
      <div className="draft__head">
        <h2 id={headingId} className="key">
          EN · müşteriye gider
        </h2>
        <button
          type="button"
          className="icon-hit"
          aria-label="İngilizce cevabı kopyala"
          onClick={() => {
            void copyText(proposal.draft.en).then((ok) =>
              store.showToast(ok ? 'İngilizce metin kopyalandı' : 'Kopyalanamadı', { icon: ok ? 'check' : 'alert' }),
            );
          }}
        >
          <span className="icon-hit__box">
            <Icon name="copy" size={16} />
          </span>
        </button>
      </div>
      <p lang="en" className="draft__en">
        {proposal.draft.en}
      </p>
      <div className="draft__divider" aria-hidden="true" />
      <h3 className="key ink-2">TR · sadece sana</h3>
      <p className="draft__tr">{proposal.draft.tr}</p>
    </section>
  );
}

export function DiffBlock({ proposal }: { proposal: ListingTitleProposal }) {
  return (
    <section className="diff" aria-label="Başlık değişikliği">
      <div className="diff__grid">
        <span className="key ink-2 diff__key">Önce</span>
        <del lang="en" className="diff__before">
          {proposal.diff.before}
        </del>
        <span className="key diff__key diff__key--after">Sonra</span>
        <ins lang="en" className="diff__after">
          {proposal.diff.after}
        </ins>
      </div>
      <p className="diff__why">{proposal.diff.rationale}</p>
    </section>
  );
}

/**
 * Generic card (2.10 row 13): every action type without a card of its own. The fields,
 * preview and diff come from the API presenter; the panel only lays them out.
 */
export function GenericFields({ proposal }: { proposal: GenericProposal }) {
  if (proposal.fields.length === 0) return null;
  return (
    <dl className="terms" aria-label="Ayrıntılar">
      {proposal.fields.map((field) => (
        <div key={field.key} className="terms__row">
          <dt className="key ink-2">{field.key}</dt>
          <dd>
            {field.value}
            {field.note ? <span className="ink-2"> · {field.note}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function GenericPreview({ proposal }: { proposal: GenericProposal }) {
  const preview = proposal.preview;
  if (!preview) return null;
  const headingId = `preview-${proposal.id}`;
  return (
    <section className="draft draft--flat" aria-labelledby={headingId}>
      <div className="draft__head">
        <h2 id={headingId} className="key">
          {preview.lang === 'en' ? 'EN · dışarı gider' : 'Önizleme'}
        </h2>
      </div>
      <p lang={preview.lang} className={preview.lang === 'en' ? 'draft__en' : 'draft__tr'}>
        {preview.text}
      </p>
    </section>
  );
}

export function GenericDiff({ proposal }: { proposal: GenericProposal }) {
  const diff = proposal.diff;
  if (!diff) return null;
  return (
    <section className="diff" aria-label="Değişiklik">
      <div className="diff__grid">
        <span className="key ink-2 diff__key">Önce</span>
        <del className="diff__before">{diff.before}</del>
        <span className="key diff__key diff__key--after">Sonra</span>
        <ins className="diff__after">{diff.after}</ins>
      </div>
    </section>
  );
}

/**
 * The Denetçi block (2.10 row 12): without the Denetçi's pass the database refuses the
 * approval, so the panel says so above the evidence and keeps only Reddet open.
 */
export function VerifierNotice({ proposal }: { proposal: Proposal }) {
  const verifier = proposal.verifier;
  if (verifier.verdict === 'passed') return null;
  const rejected = verifier.verdict === 'rejected';
  return (
    <p className={rejected ? 'verifier-note verifier-note--rejected' : 'verifier-note'} role="note">
      {rejected ? (
        <Icon name="x" size={14} stroke={2.5} className="danger" />
      ) : (
        <span className="warning">
          <TriangleGlyph />
        </span>
      )}
      <span>
        <strong>{rejected ? 'Denetçi geçirmedi' : 'Denetçi henüz bakmadı'}</strong>
        {verifier.reason ? ` · ${verifier.reason}` : null} · onaylanamaz, reddedebilirsin
      </span>
    </p>
  );
}

export function PolicyTrack({ value, cap }: { value: number; cap: number }) {
  const max = 25;
  const over = value > cap;
  const ticks = [0, 5, 10, 15, 20, 25];
  return (
    <div className="policy-track">
      <span className="policy-track__value mono">%{value}</span>
      <div
        className="policy-track__plot"
        role="img"
        aria-label={`İndirim yüzde ${value}, politika tavanı yüzde ${cap}`}
      >
        <span className="policy-track__cap-label key" style={{ right: `${100 - (cap / max) * 100}%` }}>
          Tavan %{cap} · politika
        </span>
        <span className="policy-track__track" />
        <span
          className={over ? 'policy-track__fill policy-track__fill--over' : 'policy-track__fill'}
          style={{ width: `${(Math.min(value, max) / max) * 100}%` }}
        />
        <span className="policy-track__cap" style={{ left: `${(cap / max) * 100}%` }} />
        {ticks.map((tick) => (
          <span
            key={tick}
            className="policy-track__tick mono"
            style={
              tick === 0
                ? { left: 0 }
                : tick === max
                  ? { right: 0 }
                  : { left: `${(tick / max) * 100}%`, transform: 'translateX(-50%)' }
            }
          >
            %{tick}
          </span>
        ))}
      </div>
    </div>
  );
}

export function CouponTerms({ proposal }: { proposal: CouponProposal }) {
  const rows: [string, string][] = [
    ['Kod', proposal.terms.code],
    ['İndirim', `%${proposal.terms.discountPct}`],
    ['Süre', `${proposal.terms.days} gün, ${proposal.terms.range}`],
    ['Kapsam', `${proposal.terms.scope}, ${proposal.terms.listingCount} ilan`],
  ];
  return (
    <dl className="terms">
      {rows.map(([key, value]) => (
        <div key={key} className="terms__row">
          <dt className="key ink-2">{key}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ReasoningLines({ items, title = 'Neden bu cevap' }: { items: Reasoning[]; title?: string }) {
  return (
    <section className="reasons" aria-label={title}>
      <h2 className="key ink-2">{title}</h2>
      {items.map((item) => (
        <div key={`${item.agent}-${item.at}`} className="reasons__line">
          <AgentGlyph shape={AGENT_SHAPES[item.agent]} className="reasons__glyph" />
          <p>
            <strong>{AGENT_NAMES[item.agent]}</strong>{' '}
            <span className="mono ink-2 reasons__time">{formatClock(item.at)}</span>{' '}
            {item.verdict === 'passed' ? (
              <Icon name="check" size={12} stroke={2.5} className="ok reasons__check" />
            ) : null}
            {item.verdict === 'passed' ? ' ' : null}
            {item.text}
            <span className="reasons__foot">{item.footnote}</span>
          </p>
        </div>
      ))}
    </section>
  );
}

export function SourceChips({ sources, row = false }: { sources: SourceRef[]; row?: boolean }) {
  if (sources.length === 0) return null;
  return (
    <section className="sources" aria-label="Dayanak">
      <h2 className="key ink-2">Dayanak</h2>
      <div className={row ? 'sources__list sources__list--row' : 'sources__list'}>
        {sources.map((source) => (
          <Link key={source.key} to="/bilgi-bankasi" className="source">
            <Icon name={source.kind === 'kb' ? 'book-open' : 'package'} size={16} />
            <span className="source__text">
              <span className="key ink-2">{source.key}</span>
              <span className="source__title ellipsis">{source.title}</span>
            </span>
            <Icon name="chevron-right" size={16} className="ink-3" />
          </Link>
        ))}
      </div>
    </section>
  );
}

export function RunLink({ run }: { run: Run }) {
  const seconds = run.finishedAt ? Math.round((run.finishedAt - run.startedAt) / 1000) : null;
  return (
    <Link to={`/ekip/${run.id}`} className="runlink">
      <Icon name="history" size={16} />
      <span className="runlink__text">
        <span className="runlink__title">
          Çalıştırma kaydı <span className="mono">#{run.id}</span>
        </span>
        <span className="runlink__meta ellipsis">
          <span className="mono">{run.steps.length}</span> adım
          {seconds !== null ? (
            <>
              {' · '}
              <span className="mono">{formatDuration(seconds)}</span>
            </>
          ) : null}
          {' · '}
          <span className="mono">{formatUsd(run.cost)}</span>
        </span>
      </span>
      <Icon name="chevron-right" size={16} className="ink-3" />
    </Link>
  );
}

export function blockerText(blocker: ApproveBlocker): { label: string; sublabel: string } | null {
  if (blocker === 'offline')
    return { label: 'Bağlantı bekleniyor', sublabel: 'Veri eski; bağlantı gelince onaylarsın' };
  if (blocker === 'stopped') return { label: 'Yürütücü durdu', sublabel: 'Acil durdurma açık; önce Devam et' };
  if (blocker === 'paused') return { label: 'Yürütücü durdu', sublabel: 'Ekip duraklatıldı; önce Devam et' };
  if (blocker === 'policy') return { label: 'Politika dışı', sublabel: 'İndirim tavanı aşıyor; onaylanamaz' };
  if (blocker === 'expired') return { label: 'Süresi doldu', sublabel: 'Öneri zaman aşımına uğradı; onaylanamaz' };
  if (blocker === 'verifier')
    return { label: 'Denetçi onayı yok', sublabel: 'Denetçi geçirmeden onaylanamaz; reddedebilirsin' };
  return null;
}

/** The hold button always says that it has to be held, even when the executor label does not. */
function holdLabel(label: string): string {
  return label.startsWith('Basılı tut') ? label : `Basılı tut: ${label}`;
}

/**
 * Phone decision bar: the primary action on top (a resting thumb lands on the secondary
 * row, and both secondaries open sheets, so a stray tap is harmless).
 */
export function DecisionBar({
  proposal,
  blocker,
  onApprove,
  onEdit,
  onReject,
  holdRef,
}: {
  proposal: Proposal;
  blocker: ApproveBlocker;
  onApprove: (via: ApproveVia) => void;
  onEdit: () => void;
  onReject: () => void;
  holdRef?: Ref<HoldHandle>;
}) {
  const store = useStore();
  const holdMs = t4HoldMs(usePanel().settings);
  const blocked = blockerText(blocker);
  const locked = blocker === 'locked';
  const executor = proposal.executor;
  const editable = canEdit(proposal);
  return (
    <fieldset className={`decision${locked ? ' decision--locked' : ''}`} aria-label="Karar">
      <p className="decision__consequence">
        <span className="key ink-2">Onaylayınca</span> {executor.consequence}
      </p>
      {requiresHold(proposal) ? (
        // Keyed by proposal: a hold started on one item can never complete on another.
        <T4HoldButton
          key={proposal.id}
          ref={holdRef}
          label={holdLabel(executor.label)}
          sublabel={executor.sublabel}
          disabledReason={blocked ? blocked.label : locked ? 'Bekle…' : null}
          onComplete={() => onApprove('hold')}
          onStart={() => store.beginHold(proposal.id)}
          durationMs={holdMs}
          onEarlyRelease={() => {
            store.cancelHold(proposal.id);
            store.showToast(`Onaylamak için ${formatSecondsLeft(holdMs).replace(',0 ', ' ')} basılı tut`, {
              icon: 'info',
            });
          }}
        />
      ) : (
        <button
          type="button"
          className="decision__primary"
          disabled={blocker !== null}
          onClick={() => onApprove('tap')}
        >
          <span className="decision__label">
            {blocked ? null : <Icon name="check" size={18} stroke={2} />}
            {blocked ? blocked.label : executor.label}
          </span>
          <span className="decision__sub">{blocked ? blocked.sublabel : executor.sublabel}</span>
        </button>
      )}
      <div className={editable ? 'decision__row' : 'decision__row decision__row--single'}>
        {editable ? (
          <button type="button" className="btn btn--secondary" onClick={onEdit} disabled={locked}>
            <Icon name="pencil" size={16} />
            Düzelt
          </button>
        ) : null}
        <button type="button" className="btn btn--secondary" onClick={onReject} disabled={locked}>
          <Icon name="x" size={16} className="danger" />
          Reddet
        </button>
      </div>
    </fieldset>
  );
}

/** Desktop decision buttons with their keyboard chips (O, D, R). */
export function DesktopDecision({
  proposal,
  blocker,
  onApprove,
  onEdit,
  onReject,
  holdRef,
}: {
  proposal: Proposal;
  blocker: ApproveBlocker;
  onApprove: (via: ApproveVia) => void;
  onEdit: () => void;
  onReject: () => void;
  holdRef?: Ref<HoldHandle>;
}) {
  const store = useStore();
  const holdMs = t4HoldMs(usePanel().settings);
  const blocked = blockerText(blocker);
  const executor = proposal.executor;
  const editable = canEdit(proposal);
  return (
    <fieldset className="ddecision" aria-label="Karar">
      {requiresHold(proposal) ? (
        <T4HoldButton
          key={proposal.id}
          ref={holdRef}
          compact
          label={holdLabel(executor.label)}
          sublabel={executor.desktopSublabel}
          disabledReason={blocked ? blocked.label : null}
          onComplete={() => onApprove('hold')}
          onStart={() => store.beginHold(proposal.id)}
          durationMs={holdMs}
          onEarlyRelease={() => {
            store.cancelHold(proposal.id);
            store.showToast(`Onaylamak için ${formatSecondsLeft(holdMs).replace(',0 ', ' ')} basılı tut`, {
              icon: 'info',
            });
          }}
          extra={<Kbd>O</Kbd>}
        />
      ) : (
        <button
          type="button"
          className="ddecision__primary"
          aria-keyshortcuts="O"
          disabled={blocker !== null}
          onClick={() => onApprove('tap')}
        >
          {blocked ? null : <Icon name="check" size={18} stroke={2} />}
          <span className="ddecision__text">
            <span className="ddecision__label">{blocked ? blocked.label : executor.label}</span>
            <span className="ddecision__sub">{blocked ? blocked.sublabel : executor.desktopSublabel}</span>
          </span>
          <Kbd onInk>O</Kbd>
        </button>
      )}
      {editable ? (
        <button type="button" className="ddecision__btn" aria-keyshortcuts="D" onClick={onEdit}>
          <Icon name="pencil" size={16} />
          Düzelt
          <Kbd>D</Kbd>
        </button>
      ) : null}
      <button type="button" className="ddecision__btn" aria-keyshortcuts="R" onClick={onReject}>
        <Icon name="x" size={16} className="danger" />
        Reddet
        <Kbd>R</Kbd>
      </button>
    </fieldset>
  );
}
